import { KeywordRef } from "../lib/db/types.js";
import { Army } from "./army.js";
import { checkCondition, ConditionData, isCondition } from "./condition.js";
import { Ruleset } from "./ruleset.js";
import { StatModifier, StatModifierData } from "./stat-modifier.js";
import { Unit } from "./unit.js";

export function findConditionalPurchaseOption(
  conditionalUnitCosts: ConditionalUnitCostData[] | undefined,
  keywordId: string,
  unit: Unit,
) {
  return conditionalUnitCosts?.find((conditionalCosts) =>
    conditionalCosts.conditions
      .filter(isCondition)
      .some(checkCondition(keywordId, unit)),
  );
}

type ConditionalUnitCostData = {
  cost: number;
  conditions: ConditionData[];
};

export type KeywordDef = {
  id: string;
  name: string;
  unit_cost: number;
  conditional_unit_costs?: ConditionalUnitCostData[];
  army_cost?: number;
  description: string;
  max_purchase_count: number;
  modifiers?: StatModifierData[];
  conditions?: ConditionData[];
};

export class Keyword {
  readonly ruleset: Ruleset;
  readonly owner: Army | Unit;
  readonly definition: KeywordDef;
  readonly ref: KeywordRef;

  constructor(
    owner: Army | Unit,
    keywordRef: KeywordRef,
    keywordDef: KeywordDef,
  ) {
    this.ruleset = owner.ruleset;
    this.ref = keywordRef;
    this.owner = owner;
    this.definition = keywordDef;
  }

  get id() {
    return this.definition.id;
  }

  get name() {
    return this.definition.name;
  }

  get isArmyKeyword() {
    return this.owner instanceof Army;
  }

  get cost() {
    const owner = this.owner;

    if (owner instanceof Army) {
      return this.definition.army_cost ?? null;
    }

    const conditionalPurchaseOption = findConditionalPurchaseOption(
      this.definition.conditional_unit_costs,
      this.id,
      owner,
    );

    return conditionalPurchaseOption?.cost ?? this.definition.unit_cost;
  }

  get summary() {
    return `${this.cost}pts · ${this.definition.description}`;
  }

  get modifiers() {
    return (
      this.definition.modifiers
        ?.map((modiferData) => StatModifier.from(this, modiferData))
        .filter((modifier) => !!modifier) ?? null
    );
  }

  static from(owner: Army | Unit, keywordRef: KeywordRef) {
    const keywordDefinition = owner.ruleset.keywordDefs.get(keywordRef.id);

    if (!keywordDefinition) {
      return null;
    }

    return new Keyword(owner, keywordRef, keywordDefinition);
  }
}
